/// Charts — the medium, the projections, the datums, the symbols.
/// Day Skipper + Coastal Skipper foundational material that the
/// chartwork techniques in `code.ts` (DR, EP, CTS) plot ONTO.
///
/// Three domains:
///
///   1. Map projections — Mercator vs gnomonic + how they map to
///      navigational use.
///   2. Datums — WGS84 vs OSGB36 vs ETRS89 + the conversion error.
///   3. Chart symbols — the Admiralty Catalogue 5011 reference,
///      organised by category, with a representative subset.

// ───────────────────────────────────────────────────────────────────
// 1. Projections
// ───────────────────────────────────────────────────────────────────

export type ProjectionId = "mercator" | "gnomonic" | "transverse-mercator" | "polyconic";

export interface Projection {
  id: ProjectionId;
  name: string;
  /// One-line gist.
  summary: string;
  /// Where + when this projection is used in marine navigation.
  use: string;
  /// Properties — what's preserved, what's distorted.
  properties: string;
  /// Practical implications for the navigator.
  navigationImpact: string;
}

export const PROJECTIONS: readonly Projection[] = Object.freeze([
  {
    id: "mercator",
    name: "Mercator projection",
    summary:
      "Cylindrical projection where lines of constant compass bearing (rhumb lines) appear as STRAIGHT LINES. The basis of almost all marine charts.",
    use:
      "Standard projection for marine charts from latitude 0° to about ±70°. Used for all Admiralty charts of UK + European waters.",
    properties:
      "Preserves ANGLES (conformal). Preserves SHAPES locally. Distorts AREAS — Greenland appears the size of Africa even though it's a fraction. Distortion increases with latitude.",
    navigationImpact:
      "A constant compass course = a straight line on the chart. Distance scale CHANGES with latitude — always measure distances using the LATITUDE scale at the latitude of interest, NEVER the longitude scale (which has variable scale).",
  },
  {
    id: "gnomonic",
    name: "Gnomonic projection",
    summary:
      "Projection onto a flat plane tangent to a single point on the globe. STRAIGHT LINES are GREAT CIRCLES (the shortest route between two points).",
    use:
      "Long-distance passage planning where great-circle routes matter. Ocean charts for transatlantic + transpacific passages. Cannot be used for coastal navigation — distortion is huge over large areas.",
    properties:
      "Preserves the SHORTEST PATH between points (great circles). Distorts everything else — directions, distances, shapes.",
    navigationImpact:
      "Plot a great-circle course on a gnomonic chart by drawing a STRAIGHT LINE. Transfer that line to a Mercator chart by picking off lat/lon coordinates of several points along it — on the Mercator the great circle is a CURVED line. Then steer rhumb-line courses between the waypoints.",
  },
  {
    id: "transverse-mercator",
    name: "Transverse Mercator projection",
    summary:
      "Mercator rotated 90° so the cylinder is tangent to a meridian rather than the equator. Used for narrow zones aligned north-south.",
    use:
      "UK Ordnance Survey grid (OSGB36) for land-based mapping. Less common on Admiralty marine charts but used on some harbour-detail charts + by national authorities.",
    properties:
      "Conformal (preserves angles). Distortion increases EAST-WEST from the central meridian; minimal NORTH-SOUTH.",
    navigationImpact:
      "Crossing a wide longitude band requires multiple zones. Used by some yacht GPS plotters with land-based map data.",
  },
  {
    id: "polyconic",
    name: "Polyconic projection",
    summary:
      "Older projection, still occasionally seen on US Coast Pilot publications + some legacy charts.",
    use:
      "Historical use; rarely seen on modern Admiralty charts. May appear on legacy NOAA charts of US coasts.",
    properties:
      "Neither conformal nor equal-area. Each parallel of latitude is drawn as a separate arc. Cumulative distortion when traversing long distances.",
    navigationImpact:
      "Not recommended for navigation. If you encounter a polyconic chart, prefer a Mercator chart of the same area.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 2. Datums
// ───────────────────────────────────────────────────────────────────

export type DatumId = "wgs84" | "etrs89" | "osgb36" | "ed50" | "nad83";

export interface ChartDatum {
  id: DatumId;
  name: string;
  /// One-line gist.
  summary: string;
  /// Where + when this datum is used.
  region: string;
  /// Approximate difference from WGS84 in metres.
  offsetFromWgs84: string;
  /// Reference model details.
  ellipsoid: string;
  /// Practical implications.
  navigationImpact: string;
}

export const DATUMS: readonly ChartDatum[] = Object.freeze([
  {
    id: "wgs84",
    name: "WGS84 — World Geodetic System 1984",
    summary:
      "The global datum used by GPS + most modern charts. Centred on the Earth's centre of mass + uses a global ellipsoid.",
    region: "Global. Default for GPS, modern Admiralty charts, electronic charts.",
    offsetFromWgs84: "Zero — this IS the reference.",
    ellipsoid: "WGS84 ellipsoid (modern definition; equivalent to GRS80 to sub-metre level).",
    navigationImpact:
      "Your GPS shows WGS84 coordinates by default. Modern paper + electronic charts use WGS84 unless explicitly labelled otherwise.",
  },
  {
    id: "etrs89",
    name: "ETRS89 — European Terrestrial Reference System 1989",
    summary:
      "European datum fixed to the European tectonic plate; effectively identical to WGS84 for navigation purposes (sub-metre difference).",
    region: "Europe (UK + EU adopted ETRS89 for survey + civil mapping).",
    offsetFromWgs84: "Sub-metre — typically < 0.5 m + drifts with continental motion. Navigationally indistinguishable from WGS84.",
    ellipsoid: "GRS80 ellipsoid (same shape as WGS84).",
    navigationImpact:
      "Modern UK charts use WGS84 directly. ETRS89 mostly seen on land-based mapping (UK OS maps). No practical difference for the helmsman.",
  },
  {
    id: "osgb36",
    name: "OSGB36 — Ordnance Survey Great Britain 1936",
    summary:
      "UK-specific datum used by UK Ordnance Survey maps (printed land maps). NOT used by modern Admiralty marine charts.",
    region: "United Kingdom — used on OS land maps printed before ~2020.",
    offsetFromWgs84: "Up to ~100 metres difference at extreme corners of the UK. Latitude offset typically 70 m southward; longitude offset typically 100 m eastward at Norfolk.",
    ellipsoid: "Airy 1830 ellipsoid — a 19th-century local fit to UK + Ireland.",
    navigationImpact:
      "If using a UK OS land map for shore navigation, your GPS (WGS84) won't match the map. Use the published OSGB36→WGS84 conversion OR a GPS that can output OSGB36.",
  },
  {
    id: "ed50",
    name: "ED50 — European Datum 1950",
    summary:
      "Older European datum, still occasionally seen on old continental European charts.",
    region: "Europe — used on charts published before adoption of WGS84.",
    offsetFromWgs84: "Up to ~150 metres in some areas. Typically 90 m east + 150 m north of WGS84 in central Europe.",
    ellipsoid: "International 1924 ellipsoid (Hayford).",
    navigationImpact:
      "Significant difference from GPS. Old charts of European waters labelled 'ED50' need either a recent reprint or a GPS chart-offset adjustment to be safe.",
  },
  {
    id: "nad83",
    name: "NAD83 — North American Datum 1983",
    summary:
      "North American equivalent of ETRS89. Used by US + Canadian official mapping.",
    region: "United States + Canada + Mexico.",
    offsetFromWgs84: "Sub-metre difference; navigationally indistinguishable.",
    ellipsoid: "GRS80 ellipsoid.",
    navigationImpact:
      "Modern US + Canadian charts use NAD83 OR WGS84; the offset is negligible. Cross-border navigation with WGS84 GPS works fine.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 3. Chart symbols — selection from Admiralty Catalogue 5011
// ───────────────────────────────────────────────────────────────────

export type SymbolCategory =
  | "natural-features"
  | "cultural-features"
  | "depths-and-soundings"
  | "rocks-and-wrecks"
  | "lights"
  | "buoyage"
  | "services"
  | "miscellaneous";

export interface ChartSymbol {
  id: string;
  /// 5011 reference number (e.g. "B22").
  ref: string;
  /// Display name.
  name: string;
  category: SymbolCategory;
  /// What it represents on the chart.
  description: string;
  /// Operational meaning for the navigator.
  meaning: string;
}

export const CHART_SYMBOLS: readonly ChartSymbol[] = Object.freeze([
  // Natural features
  {
    id: "shoreline",
    ref: "C8",
    name: "Shoreline (MHW)",
    category: "natural-features",
    description: "Solid line marking the Mean High Water line — the boundary between sea + land at average high tide.",
    meaning: "Reference line for all chart heights. Heights (lights, hills) measured from MHW; depths measured from Chart Datum.",
  },
  {
    id: "drying-line",
    ref: "C13",
    name: "Drying line",
    category: "natural-features",
    description: "Dashed line marking the limit of drying features — boundary of water at Mean Low Water Springs.",
    meaning: "Between MHW + drying line: sometimes-covered-by-water area. Plan timing of arrival vs tide height.",
  },
  // Cultural features
  {
    id: "pier",
    ref: "F14",
    name: "Pier",
    category: "cultural-features",
    description: "Solid line projecting into the water from the shoreline; may have a sounding or 'Q' (questionable) annotation.",
    meaning: "Berthing structure. Approach with caution + check the harbour-master arrangement; many piers are private or commercial.",
  },
  {
    id: "marina",
    ref: "F1",
    name: "Marina",
    category: "cultural-features",
    description: "Polygonal area with 'Marina' or anchor symbol; pontoons may be indicated.",
    meaning: "Recreational berthing. Check almanac for facilities + contact channel.",
  },
  // Depths + soundings
  {
    id: "sounding-numeric",
    ref: "I10",
    name: "Sounding",
    category: "depths-and-soundings",
    description: "Numeric value at a position on the chart — depth in metres below Chart Datum.",
    meaning: "Available depth at LOWEST tide. Add the height of tide to get the actual depth.",
  },
  {
    id: "drying-height",
    ref: "I15",
    name: "Drying height (underlined number)",
    category: "depths-and-soundings",
    description: "Number with an underline — height of a feature above Chart Datum, exposed at LOW tide.",
    meaning: "Drying rock or sandbank. Subtract from height of tide to know if it's covered.",
  },
  {
    id: "depth-contour",
    ref: "I30",
    name: "Depth contour",
    category: "depths-and-soundings",
    description: "Solid (or dotted) line connecting points of equal depth. Typically marked at 2m, 5m, 10m, 20m, etc.",
    meaning: "Contour for navigation safety + bottom-shape inference. Contour-following navigation = follow a specific depth line.",
  },
  // Rocks and wrecks
  {
    id: "rock-always-exposed",
    ref: "K11",
    name: "Rock — always exposed",
    category: "rocks-and-wrecks",
    description: "Cross or asterisk symbol with height value next to it.",
    meaning: "Permanent obstruction above water. Mark as a known feature on your route + check for proximity.",
  },
  {
    id: "rock-awash",
    ref: "K12",
    name: "Rock — awash at chart datum",
    category: "rocks-and-wrecks",
    description: "Cross or asterisk symbol with NO height value.",
    meaning: "Rock just at sea level at lowest tide. ALWAYS dangerous in any swell or wave; even when 'covered' there's no margin.",
  },
  {
    id: "rock-dangerous",
    ref: "K13",
    name: "Rock — dangerous below chart datum",
    category: "rocks-and-wrecks",
    description: "Cross or asterisk symbol with depth value showing how deep the rock is below CD.",
    meaning: "Submerged rock that COULD strike a deep-draft vessel at low tide. Check the depth value vs your draft + the tide.",
  },
  {
    id: "wreck-dangerous",
    ref: "K28",
    name: "Wreck — dangerous (showing above CD)",
    category: "rocks-and-wrecks",
    description: "Wreck symbol (often a stylised hull) with dot-dashed circle indicating the wreck's extent.",
    meaning: "Submerged or partly-submerged wreck. Avoid the marked area.",
  },
  // Lights
  {
    id: "lighthouse",
    ref: "P1",
    name: "Lighthouse",
    category: "lights",
    description: "Star / asterisk symbol with light characteristic in BRACKETS — Fl(2)W.10s = Flashing 2 white every 10 seconds.",
    meaning: "Major navigation light. Read the characteristic to identify; visible range usually printed.",
  },
  {
    id: "minor-light",
    ref: "P2",
    name: "Minor light",
    category: "lights",
    description: "Smaller star symbol with abbreviated characteristic.",
    meaning: "Less powerful navigation light — typically a fixed-base lamp on a small structure.",
  },
  // Buoyage
  {
    id: "lateral-port",
    ref: "Q140",
    name: "Lateral mark — port hand",
    category: "buoyage",
    description: "Red can (Region A) OR red conical (Region B) symbol with characteristic label.",
    meaning: "Keep on the appropriate side when entering harbour. Colour + shape + flash characteristic identify it.",
  },
  {
    id: "lateral-starboard",
    ref: "Q141",
    name: "Lateral mark — starboard hand",
    category: "buoyage",
    description: "Green can (Region A) OR green can (Region B) symbol with characteristic label.",
    meaning: "Keep on the appropriate side when entering harbour. Colour + shape + flash characteristic identify it.",
  },
  {
    id: "cardinal-north",
    ref: "Q130",
    name: "Cardinal mark — north",
    category: "buoyage",
    description: "Black above yellow body with double-cone topmark pointing up.",
    meaning: "Pass NORTH of this mark to stay clear of the danger it marks.",
  },
  // Services
  {
    id: "anchorage",
    ref: "N10",
    name: "Anchorage area",
    category: "services",
    description: "Dashed-line polygon with anchor symbol.",
    meaning: "Designated anchoring area. Inspect details — sometimes restricted use (visitor only, commercial only).",
  },
  {
    id: "mooring-buoy",
    ref: "Q40",
    name: "Mooring buoy",
    category: "services",
    description: "Small inverted-T symbol; may have ID number alongside.",
    meaning: "Public or visitor mooring buoy. Often requires harbour-master contact.",
  },
  // Miscellaneous
  {
    id: "obstruction",
    ref: "K40",
    name: "Obstruction",
    category: "miscellaneous",
    description: "Solid circle with surrounding dashed line. May have OBST label.",
    meaning: "Unspecified hazard or known obstruction. Treat as a 'do not approach' marker.",
  },
  {
    id: "submarine-cable",
    ref: "L25",
    name: "Submarine cable",
    category: "miscellaneous",
    description: "Dashed zigzag line across the seabed; may be labelled CABLES or have 'X' markers indicating crossings.",
    meaning: "Power or comms cable on the seabed. Avoid anchoring across or near — anchor strikes are a serious problem.",
  },
]);

export const CHART_SYMBOL_IDS: readonly string[] = CHART_SYMBOLS.map(
  (s) => s.id,
);
